import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { canFormOrEditTeams, canManageScrimmageStats, getPracticeEndDate, isPracticeSessionEnded, sortUpcomingPractices } from '../utils/practiceSchedule';
import { getPlanBadge, canEditPracticePlanForClub, isClubAdminForClub } from '../utils/practicePlan';
import {
  DEFAULT_FORMATION_CONFIG,
  buildFormationConfigPayload,
  mapSeriesFormationConfig,
  getFormationBadge,
  canFormTeamsFromRsvps,
} from '../utils/formationConfig';
import { soccerPath, soccerUrl } from '../utils/paths';
import { QRCodeSVG } from 'qrcode.react';
import './PracticeScheduling.css';
import './PracticePlanSection.css';

const DAY_OPTIONS = [
  { value: 1, label: 'Monday', shortLabel: 'Mon' },
  { value: 2, label: 'Tuesday', shortLabel: 'Tue' },
  { value: 3, label: 'Wednesday', shortLabel: 'Wed' },
  { value: 4, label: 'Thursday', shortLabel: 'Thu' },
  { value: 5, label: 'Friday', shortLabel: 'Fri' },
  { value: 6, label: 'Saturday', shortLabel: 'Sat' },
  { value: 7, label: 'Sunday', shortLabel: 'Sun' },
];

const EMPTY_RECURRING_FORM = {
  title: '',
  description: '',
  startDate: '',
  endDate: '',
  practiceTime: '',
  location: '',
  videoLinks: [],
  daysOfWeek: [],
  formationConfig: { ...DEFAULT_FORMATION_CONFIG },
};

const FormationConfigFields = ({ config, onChange }) => {
  const update = (field, value) => {
    onChange((prev) => ({
      ...prev,
      formationConfig: {
        ...(prev.formationConfig || DEFAULT_FORMATION_CONFIG),
        [field]: value,
      },
    }));
  };

  const fc = config?.formationConfig || DEFAULT_FORMATION_CONFIG;

  return (
    <div className="formation-settings">
      <h4>Team formation settings</h4>
      <p className="formation-settings__help">
        Configure once for the series. Teams can be formed automatically from RSVP votes before each practice.
      </p>
      <div className="formation-settings__grid">
        <label>
          Number of teams
          <input
            type="number"
            min="2"
            max="6"
            value={fc.numberOfTeams}
            onChange={(e) => update('numberOfTeams', Number(e.target.value))}
          />
        </label>
        <label>
          Min players to form
          <input
            type="number"
            min="2"
            max="50"
            value={fc.minPlayersRequired}
            onChange={(e) => update('minPlayersRequired', Number(e.target.value))}
          />
        </label>
        <label>
          Player pool
          <select
            value={fc.playerPool}
            onChange={(e) => update('playerPool', e.target.value)}
          >
            <option value="AVAILABLE_ONLY">Available only</option>
            <option value="AVAILABLE_AND_RESERVE">Available + Reserve</option>
          </select>
        </label>
        <label>
          Auto-form hours before practice
          <input
            type="number"
            min="1"
            max="168"
            value={fc.autoFormHoursBefore}
            onChange={(e) => update('autoFormHoursBefore', Number(e.target.value))}
            disabled={!fc.autoFormEnabled}
          />
        </label>
      </div>
      <label className="formation-settings__checkbox">
        <input
          type="checkbox"
          checked={!!fc.autoFormEnabled}
          onChange={(e) => update('autoFormEnabled', e.target.checked)}
        />
        Automatically form teams from RSVPs before each practice
      </label>
      <label className="formation-settings__checkbox">
        <input
          type="checkbox"
          checked={fc.autoPublishTeams !== false}
          onChange={(e) => update('autoPublishTeams', e.target.checked)}
        />
        Auto-publish teams to the public practice page when formed
      </label>
    </div>
  );
};

const formatDaysOfWeek = (days) => {
  if (!Array.isArray(days) || days.length === 0) return '';
  return DAY_OPTIONS
    .filter((day) => days.includes(day.value))
    .map((day) => day.shortLabel)
    .join(', ');
};

const PracticeScheduling = () => {
  const { id: clubId } = useParams();
  const navigate = useNavigate();
  const { user, isClubAdmin, isAdmin } = useAuth();
  const [practices, setPractices] = useState([]);
  const [club, setClub] = useState(null);
  const [userMember, setUserMember] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [scheduleMode, setScheduleMode] = useState('single');
  const [practiceSeries, setPracticeSeries] = useState([]);
  const [editingSeriesId, setEditingSeriesId] = useState(null);
  const [recurringFormData, setRecurringFormData] = useState({ ...EMPTY_RECURRING_FORM });
  const [editSeriesFormData, setEditSeriesFormData] = useState({ ...EMPTY_RECURRING_FORM });
  const [recurringVideoLink, setRecurringVideoLink] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    practiceDate: '',
    practiceTime: '',
    location: '',
    videoLinks: [],
    playerCapacity: '',
  });
  const [newVideoLink, setNewVideoLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showShareMenu, setShowShareMenu] = useState({});
  const [showQRCode, setShowQRCode] = useState({});
  const [editingPracticeId, setEditingPracticeId] = useState(null);
  const [practiceSummaries, setPracticeSummaries] = useState({});
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    practiceDate: '',
    practiceTime: '',
    location: '',
    videoLinks: [],
    playerCapacity: '',
  });
  const [editNewVideoLink, setEditNewVideoLink] = useState('');
  const [creatingBlogForPracticeId, setCreatingBlogForPracticeId] = useState(null);
  const [voteTokensByPractice, setVoteTokensByPractice] = useState({});
  const [voteTokensLoading, setVoteTokensLoading] = useState({});
  const [showVoteTokens, setShowVoteTokens] = useState({});

  const loadClubData = useCallback(async () => {
    try {
      const clubData = await api.getClubById(clubId);
      setClub(clubData);
      
      if (user) {
        const members = await api.getClubMembers(clubId, null, user.id);
        const member = members.find(m => 
          m.player?.userId === user.id || 
          (m.player && m.player.id && m.player.email === user.email)
        );
        if (member) {
          setUserMember(member);
        }
      }
    } catch (err) {
      console.error('Failed to load club data:', err);
    }
  }, [clubId, user]);

  const canShareLink = () => {
    if (!user) return false;
    if (isClubAdmin() && club?.adminUserId === user.id) return true;
    if (userMember?.roles?.includes('PRACTICE_COORDINATOR')) return true;
    return false;
  };

  const canEditPractice = (practice) => {
    if (!user) return false;
    if (practice.createdByUserId === user.id) return true;
    if (club && club.adminUserId === user.id) return true;
    if (userMember?.roles?.includes('PRACTICE_COORDINATOR')) return true;
    return false;
  };

  const canDeletePractice = (practice) => {
    if (!user) return false;
    if (practice.createdByUserId === user.id) return true;
    if (club && club.adminUserId === user.id) return true;
    if (userMember?.roles?.includes('PRACTICE_COORDINATOR')) return true;
    return false;
  };

  const canManagePracticePlan = () => {
    if (!user) return false;
    if (isAdmin()) return true;
    if (isClubAdminForClub(club, user)) return true;
    return userMember?.roles?.includes('PRACTICE_SPECIALIST');
  };

  const canEditPlanForPractice = () =>
    canEditPracticePlanForClub(club, user, userMember, isAdmin);

  const canManageBlog = () => {
    if (!user?.id) return false;
    if (isAdmin()) return true;
    if (isClubAdmin() && club?.adminUserId === user.id) return true;
    if (club?.adminUserId === user.id) return true;
    return false;
  };

  const handleFormTeamsFromRsvps = async (practice) => {
    if (!user?.id || !practice?.id) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await api.formTeamsFromRsvps(practice.id, user.id);
      setSuccess('Teams formed from RSVP votes');
      loadPractices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to form teams from RSVPs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBlogDraft = async (practiceId) => {
    if (!clubId || !user?.id) return;
    try {
      setCreatingBlogForPracticeId(practiceId);
      setError('');
      const post = await api.createBlogFromPracticeSummary(clubId, practiceId, user.id);
      navigate(soccerPath(`/clubs/${clubId}/admin/blog/${post.id}/edit`));
    } catch (err) {
      setError(err.message || 'Failed to create blog draft');
    } finally {
      setCreatingBlogForPracticeId(null);
    }
  };

  const handlePublishTeams = async (practice, published) => {
    if (!user?.id || !practice?.id) return;
    try {
      setLoading(true);
      setError('');
      await api.setTeamsPublishedToPublic(practice.id, user.id, published);
      setSuccess(
        published
          ? 'Teams published to the public practice page.'
          : 'Teams hidden from the public page.'
      );
      loadPractices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update public team visibility');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to properly encode message for WhatsApp with emoji support
  // Using api.whatsapp.com instead of wa.me for better emoji handling
  const encodeWhatsAppMessage = (message) => {
    // encodeURIComponent properly handles UTF-8 emojis
    return encodeURIComponent(message);
  };

  // Get WhatsApp URL - use api.whatsapp.com for better emoji support
  const getWhatsAppUrl = (message) => {
    const encodedMessage = encodeWhatsAppMessage(message);
    // Use api.whatsapp.com which handles emojis better than wa.me
    return `https://api.whatsapp.com/send?text=${encodedMessage}`;
  };

  // Create optimized WhatsApp message template
  const createWhatsAppMessage = (practice) => {
    const baseUrl = soccerUrl(`/practices/${practice.id}`);
    const date = new Date(practice.practiceDate);
    
    const dateStr = date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });

    const voteUrl = `${baseUrl}?source=whatsapp&shared=${Date.now()}`;

    return {
      title: practice.title,
      date: dateStr,
      time: timeStr,
      location: practice.location || 'TBA',
      description: practice.description || '',
      voteUrl: voteUrl,
      formatted: `⚽ *${practice.title}*\n\n` +
        `📅 *Date:* ${dateStr} at ${timeStr}\n` +
        (practice.location ? `📍 *Location:* ${practice.location}\n` : '') +
        (practice.description ? `\n📝 ${practice.description.substring(0, 100)}${practice.description.length > 100 ? '...' : ''}\n` : '') +
        `\n🗳️ *Vote your availability:*\n${voteUrl}`
    };
  };

  const shareVoteLink = (practiceId) => {
    const practice = practices.find(p => p.id === practiceId);
    if (!practice) return;

    const messageData = createWhatsAppMessage(practice);
    // Use api.whatsapp.com for better emoji support
    const whatsappUrl = getWhatsAppUrl(messageData.formatted);
    
    // Copy to clipboard
    navigator.clipboard.writeText(messageData.voteUrl).then(() => {
      setSuccess('Vote link copied to clipboard!');
      setTimeout(() => setSuccess(''), 3000);
    }).catch(() => {
      setError('Failed to copy link');
      setTimeout(() => setError(''), 3000);
    });

    // Open WhatsApp
    setTimeout(() => {
      window.open(whatsappUrl, '_blank');
    }, 500);
  };

  const shareToMultiple = (practiceId) => {
    const practice = practices.find(p => p.id === practiceId);
    if (!practice) return;

    const messageData = createWhatsAppMessage(practice);
    const whatsappUrl = getWhatsAppUrl(messageData.formatted);
    
    // Show instructions
    alert('Click "Share Link" in WhatsApp to send to multiple contacts/groups');
    window.open(whatsappUrl, '_blank');
  };

  const copyLink = (practiceId) => {
    const practice = practices.find(p => p.id === practiceId);
    if (!practice) return;

    const messageData = createWhatsAppMessage(practice);
    navigator.clipboard.writeText(messageData.voteUrl).then(() => {
      setSuccess('Link copied to clipboard!');
      setShowShareMenu({ ...showShareMenu, [practiceId]: false });
      setTimeout(() => setSuccess(''), 3000);
    }).catch(() => {
      setError('Failed to copy link');
      setTimeout(() => setError(''), 3000);
    });
  };

  const sharePublicPracticePage = async (practice) => {
    if (!user?.id || !practice) return;
    try {
      const share = await api.ensurePracticeFormationShare(practice.id, user.id);
      const token = share?.formationShareToken;
      if (!token) {
        setError('Could not create public practice page link');
        return;
      }
      const url = api.sharePublicPracticePageViaWhatsApp({
        title: practice.title,
        practiceDate: practice.practiceDate,
        location: practice.location,
        shareToken: token,
        clubName: club?.name,
        teamsFormed: !!practice.teamsPublishedToPublic,
      });
      window.open(url, '_blank');
      setSuccess('Public practice page link ready to share');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to share public practice page');
      setTimeout(() => setError(''), 4000);
    }
  };

  const copyPublicPracticePageLink = async (practice) => {
    if (!user?.id || !practice) return;
    try {
      const share = await api.ensurePracticeFormationShare(practice.id, user.id);
      const token = share?.formationShareToken;
      if (!token) {
        setError('Could not create public practice page link');
        return;
      }
      const link = soccerUrl(`/public/practice-formation/${token}`);
      await navigator.clipboard.writeText(link);
      setSuccess('Public practice page link copied!');
      setShowShareMenu({ ...showShareMenu, [practice.id]: false });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to copy public practice page link');
      setTimeout(() => setError(''), 4000);
    }
  };

  const loadVoteTokens = async (practiceId) => {
    if (!user?.id) return;
    try {
      setVoteTokensLoading((prev) => ({ ...prev, [practiceId]: true }));
      const tokens = await api.getPracticeVoteTokens(practiceId, user.id);
      setVoteTokensByPractice((prev) => ({ ...prev, [practiceId]: tokens }));
    } catch (err) {
      setError(err.message || 'Failed to load personal vote links');
    } finally {
      setVoteTokensLoading((prev) => ({ ...prev, [practiceId]: false }));
    }
  };

  const generateVoteTokens = async (practiceId) => {
    if (!user?.id) return;
    try {
      setVoteTokensLoading((prev) => ({ ...prev, [practiceId]: true }));
      const tokens = await api.generatePracticeVoteTokens(practiceId, user.id);
      setVoteTokensByPractice((prev) => ({ ...prev, [practiceId]: tokens }));
      setShowVoteTokens((prev) => ({ ...prev, [practiceId]: true }));
      setSuccess('Personal vote links generated for all approved members!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to generate vote links');
    } finally {
      setVoteTokensLoading((prev) => ({ ...prev, [practiceId]: false }));
    }
  };

  const getVoteUrl = (tokenEntry) => {
    if (tokenEntry?.voteUrl && tokenEntry.voteUrl.startsWith('http')) {
      return tokenEntry.voteUrl;
    }
    const token = tokenEntry?.token;
    const practiceId = tokenEntry?.practiceId;
    if (!token || !practiceId) return '';
    return soccerUrl(`/practices/${practiceId}?token=${encodeURIComponent(token)}`);
  };

  const copyVoteTokenLink = (tokenEntry) => {
    const url = getVoteUrl(tokenEntry);
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setSuccess(`Copied link for ${tokenEntry.playerName}`);
      setTimeout(() => setSuccess(''), 3000);
    }).catch(() => {
      setError('Failed to copy link');
      setTimeout(() => setError(''), 3000);
    });
  };

  const shareVoteTokenViaWhatsApp = (practice, tokenEntry) => {
    const url = getVoteUrl(tokenEntry);
    if (!url || !practice) return;

    const date = new Date(practice.practiceDate);
    const dateStr = date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });

    const message =
      `⚽ *${practice.title}*\n\n` +
      `Hi ${tokenEntry.playerName}! Please vote your availability.\n\n` +
      `📅 ${dateStr} at ${timeStr}\n` +
      (practice.location ? `📍 ${practice.location}\n` : '') +
      `\n🗳️ *Your personal vote link:*\n${url}`;

    window.open(getWhatsAppUrl(message), '_blank');
  };

  const toggleVoteTokensPanel = async (practiceId) => {
    const willShow = !showVoteTokens[practiceId];
    setShowVoteTokens((prev) => ({ ...prev, [practiceId]: willShow }));
    if (willShow && !voteTokensByPractice[practiceId]) {
      await loadVoteTokens(practiceId);
    }
  };

  const toggleQRCode = (practiceId) => {
    setShowQRCode({ ...showQRCode, [practiceId]: !showQRCode[practiceId] });
    setShowShareMenu({ ...showShareMenu, [practiceId]: false });
  };

  const scheduleReminder = (practiceId) => {
    const practice = practices.find(p => p.id === practiceId);
    if (!practice) return;

    const practiceDate = new Date(practice.practiceDate);
    const reminderDate = new Date(practiceDate);
    reminderDate.setDate(reminderDate.getDate() - 1); // 1 day before

    const messageData = createWhatsAppMessage(practice);

    // Create calendar event link
    const calendarDate = practiceDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(practice.title)}&dates=${calendarDate}/${calendarDate}&details=${encodeURIComponent(messageData.voteUrl)}`;

    if (window.confirm(`Set a reminder for ${reminderDate.toLocaleString()}?`)) {
      window.open(calendarUrl, '_blank');
    }
  };

  const loadPractices = useCallback(async () => {
    try {
      const data = await api.getPracticesByClub(clubId);
      setPractices(data);
    } catch (err) {
      setError(err.message || 'Failed to load practices');
    }
  }, [clubId]);

  const loadPracticeSeries = useCallback(async () => {
    try {
      const data = await api.getPracticeSeriesByClub(clubId);
      setPracticeSeries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load practice series:', err);
      setPracticeSeries([]);
    }
  }, [clubId]);

  const loadSummaries = useCallback(async () => {
    const summaries = {};
    for (const practice of practices) {
      const isPast = isPracticeSessionEnded(practice);
      const canHaveSummary = practice.status === 'COMPLETED' || (practice.status === 'SCHEDULED' && isPast);
      
      if (canHaveSummary) {
        try {
          const summary = await api.getPracticeSummary(practice.id);
          summaries[practice.id] = summary;
        } catch (err) {
          // No summary exists yet, that's okay
        }
      }
    }
    setPracticeSummaries(summaries);
  }, [practices]);

  useEffect(() => {
    loadPractices();
    loadPracticeSeries();
    loadClubData();
  }, [loadPractices, loadPracticeSeries, loadClubData]);

  useEffect(() => {
    // Load summaries for all practices
    if (practices.length > 0) {
      loadSummaries();
    }
  }, [practices, loadSummaries]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRecurringChange = (e) => {
    const { name, value } = e.target;
    setRecurringFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSeriesChange = (e) => {
    const { name, value } = e.target;
    setEditSeriesFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleRecurringDay = (dayValue, isEdit = false) => {
    const updater = (prev) => {
      const current = prev.daysOfWeek || [];
      const next = current.includes(dayValue)
        ? current.filter((day) => day !== dayValue)
        : [...current, dayValue].sort((a, b) => a - b);
      return { ...prev, daysOfWeek: next };
    };
    if (isEdit) {
      setEditSeriesFormData(updater);
    } else {
      setRecurringFormData(updater);
    }
  };

  const buildSeriesPayload = (data) => ({
    title: data.title,
    description: data.description,
    startDate: data.startDate,
    endDate: data.endDate,
    practiceTime: data.practiceTime,
    daysOfWeek: data.daysOfWeek,
    location: data.location,
    videoLinks: data.videoLinks || [],
    formationConfig: buildFormationConfigPayload(data.formationConfig || DEFAULT_FORMATION_CONFIG),
  });

  const handleRecurringSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!recurringFormData.startDate || !recurringFormData.endDate || !recurringFormData.practiceTime) {
        setError('Please provide start date, end date, and practice time');
        setLoading(false);
        return;
      }
      if (!recurringFormData.daysOfWeek || recurringFormData.daysOfWeek.length === 0) {
        setError('Select at least one day of the week');
        setLoading(false);
        return;
      }

      const response = await api.createPracticeSeries(
        clubId,
        buildSeriesPayload(recurringFormData),
        user.id
      );
      setSuccess(`Recurring practice created (${response.generatedPracticeCount} sessions scheduled).`);
      setRecurringFormData({ ...EMPTY_RECURRING_FORM });
      setRecurringVideoLink('');
      setShowForm(false);
      loadPractices();
      loadPracticeSeries();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to create recurring practice');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSeries = (series) => {
    setEditingSeriesId(series.id);
    setEditSeriesFormData({
      title: series.title || '',
      description: series.description || '',
      startDate: series.startDate || '',
      endDate: series.endDate || '',
      practiceTime: series.practiceTime || '',
      location: series.location || '',
      videoLinks: series.videoLinks || [],
      daysOfWeek: series.daysOfWeek || [],
      formationConfig: mapSeriesFormationConfig(series),
    });
    setShowForm(false);
    setEditingPracticeId(null);
  };

  const handleEditSeriesSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!editSeriesFormData.startDate || !editSeriesFormData.endDate || !editSeriesFormData.practiceTime) {
        setError('Please provide start date, end date, and practice time');
        setLoading(false);
        return;
      }
      if (!editSeriesFormData.daysOfWeek || editSeriesFormData.daysOfWeek.length === 0) {
        setError('Select at least one day of the week');
        setLoading(false);
        return;
      }

      const response = await api.updatePracticeSeries(
        editingSeriesId,
        buildSeriesPayload(editSeriesFormData),
        user.id
      );
      setSuccess(`Recurring practice updated (${response.generatedPracticeCount} total sessions).`);
      setEditingSeriesId(null);
      setEditSeriesFormData({ ...EMPTY_RECURRING_FORM });
      loadPractices();
      loadPracticeSeries();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to update recurring practice');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSeriesEdit = () => {
    setEditingSeriesId(null);
    setEditSeriesFormData({ ...EMPTY_RECURRING_FORM });
  };

  const handleDeleteSeries = async (series, deleteAll = true) => {
    const scopeLabel = deleteAll ? 'all sessions in this series' : 'upcoming sessions in this series';
    if (!window.confirm(`Delete "${series.title}" and ${scopeLabel}? This cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.deletePracticeSeries(series.id, user.id, deleteAll);
      setSuccess(deleteAll ? 'Recurring practice series deleted.' : 'Upcoming sessions deleted.');
      if (editingSeriesId === series.id) {
        setEditingSeriesId(null);
      }
      loadPractices();
      loadPracticeSeries();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete recurring practice');
    } finally {
      setLoading(false);
    }
  };

  const canManageSeries = () => canShareLink();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Combine date and time inputs into LocalDateTime format (without timezone)
      // Date input returns "YYYY-MM-DD", time input returns "HH:mm"
      // We send it as LocalDateTime string so backend treats it as local time
      let practiceDateISO = null;
      if (formData.practiceDate && formData.practiceTime) {
        // Format as LocalDateTime string: "YYYY-MM-DDTHH:mm:ss"
        // This will be parsed by Jackson as LocalDateTime without timezone conversion
        practiceDateISO = `${formData.practiceDate}T${formData.practiceTime}:00`;
      }
      
      if (!practiceDateISO) {
        setError('Please select both date and time');
        setLoading(false);
        return;
      }
      
      const practiceData = {
        title: formData.title,
        description: formData.description,
        practiceDate: practiceDateISO,
        location: formData.location,
        videoLinks: formData.videoLinks || [],
        playerCapacity: formData.playerCapacity ? parseInt(formData.playerCapacity, 10) : null,
      };
      await api.createPractice(clubId, practiceData, user.id);
      setSuccess('Practice scheduled successfully!');
      setFormData({ title: '', description: '', practiceDate: '', practiceTime: '', location: '', videoLinks: [], playerCapacity: '' });
      setNewVideoLink('');
      setShowForm(false);
      loadPractices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to schedule practice');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (practice) => {
    setEditingPracticeId(practice.id);
    // Format date and time for separate inputs
    // The backend sends LocalDateTime which Jackson serializes as ISO string
    // We need to parse it and convert to local time for display
    const dateString = practice.practiceDate;
    let practiceDate;
    
    // Check if it's an ISO string with timezone (ends with Z or has timezone offset)
    if (dateString.includes('Z') || dateString.match(/[+-]\d{2}:\d{2}$/)) {
      // It's UTC, parse and convert to local
      practiceDate = new Date(dateString);
    } else {
      // It's LocalDateTime format (YYYY-MM-DDTHH:mm:ss), parse directly
      const [datePart, timePart] = dateString.split('T');
      if (datePart && timePart) {
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes] = timePart.split(':').map(Number);
        // Create date in local timezone
        practiceDate = new Date(year, month - 1, day, hours || 0, minutes || 0, 0, 0);
      } else {
        // Fallback to Date parsing
        practiceDate = new Date(dateString);
      }
    }
    
    // Get local date/time components
    const year = practiceDate.getFullYear();
    const month = String(practiceDate.getMonth() + 1).padStart(2, '0');
    const day = String(practiceDate.getDate()).padStart(2, '0');
    const hours = String(practiceDate.getHours()).padStart(2, '0');
    const minutes = String(practiceDate.getMinutes()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    const formattedTime = `${hours}:${minutes}`;
    
    setEditFormData({
      title: practice.title || '',
      description: practice.description || '',
      practiceDate: formattedDate,
      practiceTime: formattedTime,
      location: practice.location || '',
      videoLinks: practice.videoLinks || [],
      playerCapacity: practice.playerCapacity != null ? String(practice.playerCapacity) : '',
    });
    setEditNewVideoLink('');
    setShowForm(false); // Hide create form if open
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Combine date and time inputs into LocalDateTime format (without timezone)
      // We send it as LocalDateTime string so backend treats it as local time
      let practiceDateISO = null;
      if (editFormData.practiceDate && editFormData.practiceTime) {
        // Format as LocalDateTime string: "YYYY-MM-DDTHH:mm:ss"
        // This will be parsed by Jackson as LocalDateTime without timezone conversion
        practiceDateISO = `${editFormData.practiceDate}T${editFormData.practiceTime}:00`;
      }
      
      if (!practiceDateISO) {
        setError('Please select both date and time');
        setLoading(false);
        return;
      }
      
      const practiceData = {
        title: editFormData.title,
        description: editFormData.description,
        practiceDate: practiceDateISO,
        location: editFormData.location,
        videoLinks: editFormData.videoLinks || [],
        playerCapacity: editFormData.playerCapacity ? parseInt(editFormData.playerCapacity, 10) : null,
      };
      await api.updatePractice(editingPracticeId, practiceData, user.id);
      setSuccess('Practice updated successfully!');
      setEditingPracticeId(null);
      setEditFormData({ title: '', description: '', practiceDate: '', practiceTime: '', location: '', videoLinks: [], playerCapacity: '' });
      loadPractices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update practice');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingPracticeId(null);
    setEditFormData({ title: '', description: '', practiceDate: '', practiceTime: '', location: '', videoLinks: [], playerCapacity: '' });
  };

  const handleDelete = async (practice) => {
    if (!window.confirm(`Are you sure you want to delete the practice "${practice.title}"? This action cannot be undone and will permanently remove the practice.`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.deletePractice(practice.id, user.id);
      setSuccess('Practice deleted successfully!');
      loadPractices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete practice');
    } finally {
      setLoading(false);
    }
  };

  const upcomingPractices = sortUpcomingPractices(practices);
  const practicesToShow = upcomingPractices;

  return (
    <div className="practice-scheduling">
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate(soccerPath(`/clubs/${clubId}`))}
          className="btn btn-secondary"
          style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
        >
          ← Back to Club
        </button>
        <h2 style={{ flex: '1', minWidth: '200px' }}>Practice Scheduling</h2>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <button
        onClick={() => setShowForm(!showForm)}
        className="btn btn-primary"
        style={{ marginBottom: '1rem' }}
      >
        {showForm ? 'Cancel' : 'Schedule New Practice'}
      </button>

      {showForm && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <div className="practice-schedule-mode">
            <button
              type="button"
              className={`practice-schedule-mode__btn ${scheduleMode === 'single' ? 'is-active' : ''}`}
              onClick={() => setScheduleMode('single')}
            >
              One-time practice
            </button>
            <button
              type="button"
              className={`practice-schedule-mode__btn ${scheduleMode === 'recurring' ? 'is-active' : ''}`}
              onClick={() => setScheduleMode('recurring')}
            >
              Recurring practice
            </button>
          </div>

          {scheduleMode === 'single' ? (
          <>
          <h3>Create Practice</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>Practice Date *</label>
              <input
                type="date"
                name="practiceDate"
                value={formData.practiceDate}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Practice Time *</label>
              <input
                type="time"
                name="practiceTime"
                value={formData.practiceTime}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Player capacity (Available spots)</label>
              <input
                type="number"
                name="playerCapacity"
                min="1"
                value={formData.playerCapacity}
                onChange={handleChange}
                placeholder="e.g. 22 for two teams of 11"
              />
              <small style={{ color: '#666' }}>
                Optional. Once full, extra players vote as Reserve instead of Available.
              </small>
            </div>
            <div className="form-group">
              <label>Practice Video Links (Optional)</label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  type="url"
                  placeholder="Enter video URL (YouTube, Vimeo, etc.)"
                  value={newVideoLink}
                  onChange={(e) => setNewVideoLink(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (newVideoLink.trim()) {
                      setFormData(prev => ({
                        ...prev,
                        videoLinks: [...(prev.videoLinks || []), newVideoLink.trim()]
                      }));
                      setNewVideoLink('');
                    }
                  }}
                  className="btn btn-secondary"
                >
                  Add
                </button>
              </div>
              {formData.videoLinks && formData.videoLinks.length > 0 && (
                <div style={{ marginTop: '0.5rem' }}>
                  {formData.videoLinks.map((link, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <a href={link} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: '0.9rem', wordBreak: 'break-all' }}>
                        {link}
                      </a>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            videoLinks: prev.videoLinks.filter((_, i) => i !== index)
                          }));
                        }}
                        className="btn btn-danger"
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Scheduling...' : 'Schedule Practice'}
            </button>
          </form>
          </>
          ) : (
          <>
          <h3>Create Recurring Practice</h3>
          <form onSubmit={handleRecurringSubmit}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={recurringFormData.title}
                onChange={handleRecurringChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={recurringFormData.description}
                onChange={handleRecurringChange}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                name="startDate"
                value={recurringFormData.startDate}
                onChange={handleRecurringChange}
                required
              />
            </div>
            <div className="form-group">
              <label>End Date *</label>
              <input
                type="date"
                name="endDate"
                value={recurringFormData.endDate}
                onChange={handleRecurringChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Practice Time *</label>
              <input
                type="time"
                name="practiceTime"
                value={recurringFormData.practiceTime}
                onChange={handleRecurringChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Repeat on *</label>
              <div className="practice-days-grid">
                {DAY_OPTIONS.map((day) => (
                  <label key={day.value} className="practice-day-chip">
                    <input
                      type="checkbox"
                      checked={(recurringFormData.daysOfWeek || []).includes(day.value)}
                      onChange={() => toggleRecurringDay(day.value, false)}
                    />
                    <span>{day.shortLabel}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                name="location"
                value={recurringFormData.location}
                onChange={handleRecurringChange}
              />
            </div>
            <div className="form-group">
              <label>Practice Video Links (Optional)</label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  type="url"
                  placeholder="Enter video URL (YouTube, Vimeo, etc.)"
                  value={recurringVideoLink}
                  onChange={(e) => setRecurringVideoLink(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (recurringVideoLink.trim()) {
                      setRecurringFormData((prev) => ({
                        ...prev,
                        videoLinks: [...(prev.videoLinks || []), recurringVideoLink.trim()],
                      }));
                      setRecurringVideoLink('');
                    }
                  }}
                  className="btn btn-secondary"
                >
                  Add
                </button>
              </div>
              {recurringFormData.videoLinks && recurringFormData.videoLinks.length > 0 && (
                <div style={{ marginTop: '0.5rem' }}>
                  {recurringFormData.videoLinks.map((link, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <a href={link} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: '0.9rem', wordBreak: 'break-all' }}>
                        {link}
                      </a>
                      <button
                        type="button"
                        onClick={() => {
                          setRecurringFormData((prev) => ({
                            ...prev,
                            videoLinks: prev.videoLinks.filter((_, i) => i !== index),
                          }));
                        }}
                        className="btn btn-danger"
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <FormationConfigFields config={recurringFormData} onChange={setRecurringFormData} />
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Scheduling...' : 'Create Recurring Series'}
            </button>
          </form>
          </>
          )}
        </div>
      )}

      {editingPracticeId && (
        <div className="card" style={{ marginBottom: '2rem', border: '2px solid #007bff' }}>
          <h3>Edit Practice</h3>
          <form onSubmit={handleEditSubmit}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={editFormData.title}
                onChange={handleEditChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={editFormData.description}
                onChange={handleEditChange}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>Practice Date *</label>
              <input
                type="date"
                name="practiceDate"
                value={editFormData.practiceDate}
                onChange={handleEditChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Practice Time *</label>
              <input
                type="time"
                name="practiceTime"
                value={editFormData.practiceTime}
                onChange={handleEditChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                name="location"
                value={editFormData.location}
                onChange={handleEditChange}
              />
            </div>
            <div className="form-group">
              <label>Player capacity (Available spots)</label>
              <input
                type="number"
                name="playerCapacity"
                min="1"
                value={editFormData.playerCapacity}
                onChange={handleEditChange}
                placeholder="e.g. 22 for two teams of 11"
              />
              <small style={{ color: '#666' }}>
                Optional. Once full, extra players vote as Reserve instead of Available.
              </small>
            </div>
            <div className="form-group">
              <label>Practice Video Links (Optional)</label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  type="url"
                  placeholder="Enter video URL (YouTube, Vimeo, etc.)"
                  value={editNewVideoLink}
                  onChange={(e) => setEditNewVideoLink(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (editNewVideoLink.trim()) {
                      setEditFormData(prev => ({
                        ...prev,
                        videoLinks: [...(prev.videoLinks || []), editNewVideoLink.trim()]
                      }));
                      setEditNewVideoLink('');
                    }
                  }}
                  className="btn btn-secondary"
                >
                  Add
                </button>
              </div>
              {editFormData.videoLinks && editFormData.videoLinks.length > 0 && (
                <div style={{ marginTop: '0.5rem' }}>
                  {editFormData.videoLinks.map((link, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <a href={link} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: '0.9rem', wordBreak: 'break-all' }}>
                        {link}
                      </a>
                      <button
                        type="button"
                        onClick={() => {
                          setEditFormData(prev => ({
                            ...prev,
                            videoLinks: prev.videoLinks.filter((_, i) => i !== index)
                          }));
                        }}
                        className="btn btn-danger"
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Updating...' : 'Update Practice'}
              </button>
              <button type="button" onClick={handleCancelEdit} className="btn btn-secondary" disabled={loading}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {editingSeriesId && (
        <div className="card" style={{ marginBottom: '2rem', border: '2px solid var(--color-ink)' }}>
          <h3>Edit Recurring Practice</h3>
          <form onSubmit={handleEditSeriesSubmit}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={editSeriesFormData.title}
                onChange={handleEditSeriesChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={editSeriesFormData.description}
                onChange={handleEditSeriesChange}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                name="startDate"
                value={editSeriesFormData.startDate}
                onChange={handleEditSeriesChange}
                required
              />
            </div>
            <div className="form-group">
              <label>End Date *</label>
              <input
                type="date"
                name="endDate"
                value={editSeriesFormData.endDate}
                onChange={handleEditSeriesChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Practice Time *</label>
              <input
                type="time"
                name="practiceTime"
                value={editSeriesFormData.practiceTime}
                onChange={handleEditSeriesChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Repeat on *</label>
              <div className="practice-days-grid">
                {DAY_OPTIONS.map((day) => (
                  <label key={day.value} className="practice-day-chip">
                    <input
                      type="checkbox"
                      checked={(editSeriesFormData.daysOfWeek || []).includes(day.value)}
                      onChange={() => toggleRecurringDay(day.value, true)}
                    />
                    <span>{day.shortLabel}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                name="location"
                value={editSeriesFormData.location}
                onChange={handleEditSeriesChange}
              />
            </div>
            <FormationConfigFields config={editSeriesFormData} onChange={setEditSeriesFormData} />
            <p className="practice-series-note">
              Updating a series refreshes upcoming scheduled sessions. Past sessions are kept as-is.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Updating...' : 'Update Series'}
              </button>
              <button type="button" onClick={handleCancelSeriesEdit} className="btn btn-secondary" disabled={loading}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {canManageSeries() && practiceSeries.length > 0 && (
        <div className="practice-series-list" style={{ marginBottom: '2rem' }}>
          <h3>Recurring Practice Series</h3>
          {practiceSeries.map((series) => (
            <div key={series.id} className="practice-series-card">
              <div>
                <h4>{series.title}</h4>
                <p>
                  <strong>Schedule:</strong> {formatDaysOfWeek(series.daysOfWeek)} at {series.practiceTime}
                </p>
                <p>
                  <strong>Date range:</strong> {series.startDate} to {series.endDate}
                </p>
                {series.location && <p><strong>Location:</strong> {series.location}</p>}
                <p><strong>Sessions:</strong> {series.generatedPracticeCount}</p>
                {series.formationConfig?.autoFormEnabled && (
                  <p>
                    <strong>Teams:</strong> Auto-form {series.formationConfig.autoFormHoursBefore}h before practice
                    ({series.formationConfig.numberOfTeams} teams, min {series.formationConfig.minPlayersRequired} players)
                  </p>
                )}
              </div>
              <div className="practice-series-card__actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleEditSeries(series)}
                  disabled={loading}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => handleDeleteSeries(series, false)}
                  disabled={loading}
                >
                  Delete upcoming
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => handleDeleteSeries(series, true)}
                  disabled={loading}
                >
                  Delete all
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="practices-list">
        <h3>Upcoming Practices</h3>
        {practicesToShow.length === 0 ? (
          <p>No upcoming practices scheduled.</p>
        ) : (
          practicesToShow.map((practice, index) => {
            const featured = index === 0;
            const showMoreHeading = index === 1 && practicesToShow.length > 1;
            const messageData = createWhatsAppMessage(practice);
            const planBadge = getPlanBadge(practice, { canEdit: canManagePracticePlan() });
            const formationBadge = getFormationBadge(practice);
            return (
              <React.Fragment key={practice.id}>
                {showMoreHeading && (
                  <h4 className="practices-list__subheading">More upcoming</h4>
                )}
                <div className={`practice-card${featured ? ' practice-card--featured' : ''}`}>
                {featured && <p className="practice-card__eyebrow">Next up</p>}
                <h4>
                  {practice.title}
                  {practice.seriesId && (
                    <span className="practice-series-badge">Recurring</span>
                  )}
                  <span className={planBadge.className}>{planBadge.label}</span>
                  {formationBadge && (
                    <span className={formationBadge.className}>{formationBadge.label}</span>
                  )}
                </h4>
                {practice.description && <p>{practice.description}</p>}
                <p><strong>Date:</strong> {new Date(practice.practiceDate).toLocaleString()}
                  {getPracticeEndDate(practice) && (
                    <span style={{ color: '#666' }}> — ends ~{getPracticeEndDate(practice).toLocaleString()}</span>
                  )}
                </p>
                {practice.location && <p><strong>Location:</strong> {practice.location}</p>}
                <p><strong>Status:</strong> {practice.status}</p>
                
                {(() => {
                  const isPast = isPracticeSessionEnded(practice);
                  const canShowSummary = practice.status === 'COMPLETED' || (practice.status === 'SCHEDULED' && isPast);
                  return canShowSummary && practiceSummaries[practice.id] && (
                    <div className="summary-preview" style={{ marginTop: '1rem', padding: '1rem', background: '#f0f0f0', borderRadius: '4px' }}>
                      <h5>Practice Summary</h5>
                      {practiceSummaries[practice.id].scoreHome !== null && practiceSummaries[practice.id].scoreAway !== null && (
                        <p><strong>Score:</strong> {practiceSummaries[practice.id].scoreHome} - {practiceSummaries[practice.id].scoreAway}</p>
                      )}
                      {practiceSummaries[practice.id].manOfMatchPlayerName && (
                        <p><strong>Man of the Match:</strong> {practiceSummaries[practice.id].manOfMatchPlayerName}</p>
                      )}
                      {practiceSummaries[practice.id].photoUrls && practiceSummaries[practice.id].photoUrls.length > 0 && (
                        <p><strong>Photos:</strong> {practiceSummaries[practice.id].photoUrls.length} photo(s)</p>
                      )}
                    </div>
                  );
                })()}
                
                {/* QR Code Display */}
                {showQRCode[practice.id] && (
                  <div style={{ 
                    padding: '1rem', 
                    backgroundColor: 'white', 
                    borderRadius: '8px',
                    textAlign: 'center',
                    marginTop: '1rem',
                    border: '1px solid #ddd'
                  }}>
                    <QRCodeSVG value={messageData.voteUrl} size={200} />
                    <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                      Scan to vote
                    </p>
                  </div>
                )}

                <div className="practice-card__actions">
                  <Link to={soccerPath(`/practices/${practice.id}`)} className="btn btn-secondary">
                    View Details & Vote Availability
                  </Link>
                  {canEditPlanForPractice() && (
                    <Link to={soccerPath(`/practices/${practice.id}#practice-plan`)} className="btn btn-primary">
                      Practice plan
                    </Link>
                  )}
                  
                  {(() => {
                    const isPast = isPracticeSessionEnded(practice);
                    const canCreateSummary = practice.status === 'COMPLETED' || (practice.status === 'SCHEDULED' && isPast);
                    return canCreateSummary && (canShareLink() || practice.createdByUserId === user?.id) && (
                      <Link to={soccerPath(`/practices/${practice.id}/summary`)} className="btn btn-primary">
                        {practiceSummaries[practice.id] ? 'Update Summary' : 'Create Summary'}
                      </Link>
                    );
                  })()}

                  {canManageBlog() && practiceSummaries[practice.id] && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => handleCreateBlogDraft(practice.id)}
                      disabled={creatingBlogForPracticeId === practice.id}
                    >
                      {creatingBlogForPracticeId === practice.id ? 'Creating draft…' : 'Create blog draft'}
                    </button>
                  )}
                  
                  {canEditPractice(practice) && (
                    <>
                      <button
                        onClick={() => handleEdit(practice)}
                        className="btn btn-primary"
                        style={{ backgroundColor: '#ffc107', borderColor: '#ffc107', color: '#000' }}
                        disabled={editingPracticeId === practice.id}
                      >
                        ✏️ Edit
                      </button>
                    </>
                  )}
                  
                  {canDeletePractice(practice) && (
                    <button
                      onClick={() => handleDelete(practice)}
                      className="btn btn-danger"
                      style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                      disabled={loading}
                    >
                      🗑️ Delete
                    </button>
                  )}
                  
                  {canShareLink() && (
                    <div className="practice-card__share-wrap">
                      <button
                        onClick={() => setShowShareMenu({ ...showShareMenu, [practice.id]: !showShareMenu[practice.id] })}
                        className="btn btn-primary"
                        style={{ backgroundColor: '#25D366' }}
                      >
                        📱 Share Vote Link
                      </button>
                      
                      {showShareMenu[practice.id] && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          backgroundColor: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          padding: '0.5rem',
                          marginTop: '0.5rem',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          zIndex: 1000,
                          minWidth: '200px'
                        }}>
                          <button
                            onClick={() => {
                              shareVoteLink(practice.id);
                              setShowShareMenu({ ...showShareMenu, [practice.id]: false });
                            }}
                            style={{ width: '100%', marginBottom: '0.5rem', textAlign: 'left' }}
                            className="btn btn-secondary"
                          >
                            📱 Share via WhatsApp
                          </button>
                          <button
                            onClick={() => {
                              shareToMultiple(practice.id);
                              setShowShareMenu({ ...showShareMenu, [practice.id]: false });
                            }}
                            style={{ width: '100%', marginBottom: '0.5rem', textAlign: 'left' }}
                            className="btn btn-secondary"
                          >
                            👥 Share to Multiple
                          </button>
                          <button
                            onClick={() => copyLink(practice.id)}
                            style={{ width: '100%', marginBottom: '0.5rem', textAlign: 'left' }}
                            className="btn btn-secondary"
                          >
                            📋 Copy Link
                          </button>
                          <button
                            onClick={() => toggleQRCode(practice.id)}
                            style={{ width: '100%', marginBottom: '0.5rem', textAlign: 'left' }}
                            className="btn btn-secondary"
                          >
                            📷 {showQRCode[practice.id] ? 'Hide' : 'Show'} QR Code
                          </button>
                          <button
                            onClick={() => scheduleReminder(practice.id)}
                            style={{ width: '100%', textAlign: 'left' }}
                            className="btn btn-secondary"
                          >
                            ⏰ Set Reminder
                          </button>
                          <button
                            onClick={() => {
                              toggleVoteTokensPanel(practice.id);
                              setShowShareMenu({ ...showShareMenu, [practice.id]: false });
                            }}
                            style={{ width: '100%', marginTop: '0.5rem', textAlign: 'left' }}
                            className="btn btn-secondary"
                          >
                            🔗 Personal Vote Links
                          </button>
                          <button
                            onClick={() => {
                              sharePublicPracticePage(practice);
                              setShowShareMenu({ ...showShareMenu, [practice.id]: false });
                            }}
                            style={{ width: '100%', marginTop: '0.5rem', textAlign: 'left' }}
                            className="btn btn-secondary"
                          >
                            📋 Share Public Practice Page
                          </button>
                          <button
                            onClick={() => copyPublicPracticePageLink(practice)}
                            style={{ width: '100%', marginTop: '0.5rem', textAlign: 'left' }}
                            className="btn btn-secondary"
                          >
                            🔗 Copy Public Page Link
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {canShareLink() && showVoteTokens[practice.id] && (
                    <div className="vote-tokens-panel" style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      backgroundColor: '#f8f9fa',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                        <h4 style={{ margin: 0 }}>Personal Vote Links</h4>
                        <button
                          onClick={() => generateVoteTokens(practice.id)}
                          className="btn btn-primary btn-sm"
                          disabled={voteTokensLoading[practice.id]}
                        >
                          {voteTokensLoading[practice.id] ? 'Working...' : 'Generate / Refresh Links'}
                        </button>
                      </div>
                      <p style={{ margin: '0 0 0.75rem', color: '#666', fontSize: '0.9rem' }}>
                        Each link lets one player vote without logging in.
                      </p>
                      {voteTokensLoading[practice.id] && (
                        <p>Loading vote links...</p>
                      )}
                      {!voteTokensLoading[practice.id] && (!voteTokensByPractice[practice.id] || voteTokensByPractice[practice.id].length === 0) && (
                        <p style={{ margin: 0, color: '#666' }}>No links yet. Click Generate to create links for all approved members.</p>
                      )}
                      {!voteTokensLoading[practice.id] && voteTokensByPractice[practice.id]?.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {voteTokensByPractice[practice.id].map((tokenEntry) => (
                            <div
                              key={tokenEntry.playerId}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '0.75rem',
                                flexWrap: 'wrap',
                                padding: '0.5rem',
                                background: '#fff',
                                borderRadius: '6px',
                                border: '1px solid #e9ecef',
                              }}
                            >
                              <span style={{ fontWeight: 500 }}>{tokenEntry.playerName}</span>
                              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => copyVoteTokenLink(tokenEntry)}
                                >
                                  Copy
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => shareVoteTokenViaWhatsApp(practice, tokenEntry)}
                                  style={{ backgroundColor: '#25D366', color: '#fff', borderColor: '#25D366' }}
                                >
                                  WhatsApp
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {canFormOrEditTeams(practice) && !practice.teamFormationId && canFormTeamsFromRsvps(practice) && (
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() => handleFormTeamsFromRsvps(practice)}
                      disabled={loading}
                    >
                      Form teams from RSVPs ({practice.formationSettings?.rsvpEligibleCount ?? 0})
                    </button>
                  )}
                  {canFormOrEditTeams(practice) && (
                    <Link to={soccerPath(`/practices/${practice.id}/teams`)} className="btn btn-primary">
                      {practice.teamFormationId ? 'Edit Teams' : 'Form Teams'}
                    </Link>
                  )}
                  {practice.teamFormationId && (
                    <>
                      <Link to={soccerPath(`/practices/${practice.id}/lineup`)} className="btn btn-secondary">
                        View lineup
                      </Link>
                      {canFormOrEditTeams(practice) && (
                        <button
                          type="button"
                          className={`btn ${practice.teamsPublishedToPublic ? 'btn-secondary' : 'btn-success'}`}
                          onClick={() => handlePublishTeams(practice, !practice.teamsPublishedToPublic)}
                          disabled={loading}
                        >
                          {practice.teamsPublishedToPublic ? 'Unpublish teams' : 'Publish teams'}
                        </button>
                      )}
                    </>
                  )}
                  {(canShareLink() || canEditPractice(practice)) && canManageScrimmageStats(practice) && (
                    <Link to={soccerPath(`/practices/${practice.id}/scrimmage-stats`)} className="btn btn-secondary">
                      {isPracticeSessionEnded(practice) ? '📊 Update scrimmage stats' : '📊 Live scrimmage stats'}
                    </Link>
                  )}
                </div>
              </div>
              </React.Fragment>
            );
          })
        )}
        <div className="practices-list__footer">
          <Link to={soccerPath(`/clubs/${clubId}/practices/completed`)} className="practices-list__completed-link">
            View completed practices →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PracticeScheduling;
